import { supabase } from './supabase'
import { documentFileError, documentFileType } from './documentUploadTypes'

// Retain each item's object path and document ID across retries. A response can
// fail after the server has already saved the file or row.
export async function uploadDocumentItem(item, context) {
  const { clientId, taxYear, requiresSignature, isCPA, cpaId } = context
  const fileError = documentFileError(item.file)
  if (fileError) throw new Error(fileError)
  if (!clientId || !cpaId) throw new Error('The client account is not linked to a firm. Please contact your firm before uploading.')
  if (!item.path) {
    const safeName = item.file.name.replace(/[^a-zA-Z0-9._-]/g, '_') || 'document'
    item.path = `${clientId}/${taxYear}/${item.id}_${safeName}`
  }
  if (!item.stored) {
    const { error } = await supabase.storage.from('client-documents').upload(item.path, item.file, { upsert: false, contentType: documentFileType(item.file) })
    // This randomly generated path belongs to this item. A conflict on retry
    // means its earlier upload succeeded even though the response was lost.
    const duplicate = Number(error?.statusCode || error?.status) === 409 || error?.error === 'Duplicate'
    if (error && !duplicate) throw new Error(error.message || 'The file could not be uploaded. Please retry.')
    item.stored = true
  }
  if (!item.record) item.record = {
    id: item.id,
    client_id: clientId,
    cpa_id: cpaId,
    uploaded_by: isCPA ? 'cpa' : 'client',
    file_name: item.file.name,
    file_path: item.path,
    file_size: item.file.size,
    file_type: documentFileType(item.file),
    category: item.category,
    tax_year: taxYear,
    status: isCPA ? (requiresSignature ? 'awaiting_signature' : 'sent') : 'pending',
    requires_signature: isCPA ? !!requiresSignature : false,
    signing_status: 'none',
  }

  let failure
  try {
    const { data, error } = await supabase.from('documents').insert(item.record).select('*, clients(full_name, business_name)').single()
    if (!error && data) return data
    failure = error || new Error('The document details could not be saved.')
  } catch (error) { failure = error }

  // Recover a committed insert after a lost response. Use SELECT, never upsert,
  // so retry cannot overwrite an existing document or require new permissions.
  try {
    const { data, error } = await supabase.from('documents').select('*, clients(full_name, business_name)').eq('id', item.id).maybeSingle()
    if (!error && data?.file_path === item.path && data.client_id === clientId) return data
  } catch { /* The same retained ID will be retried without creating another row. */ }
  throw new Error(failure.message || 'The file was uploaded, but its details could not be saved. Please retry.')
}
